import type { DataDemandSource } from "@/lib/column-registry";
import {
  ORDERS_WEEKS_MARKER_KEY, ORDERS_WEEK_DAY_COLUMN_WIDTH, ORDERS_WEEK_DAY_PREFIX, ORDERS_WEEK_SUM_COLUMN_WIDTH,
  ORDERS_WEEK_SUM_LABEL, ORDERS_WEEK_SUM_PREFIX, ORDERS_WEEKS_DIARY_DAYS,
  addDaysIso, buildOrdersWeeks, daysBetweenIso, isIsoDateKey, ordersWeekStartOf, type OrdersWeek,
} from "@/lib/orders-weeks-columns";

/**
 * Семейство календарей «по неделям»: заказы, остатки, цена, реклама.
 *
 * Устройство одно на всех и описано в lib/orders-weeks-columns: в наборе колонок
 * хранится маркер (`orders_weeks`, `stock_weeks`, `price_weeks`, `ads_weeks`),
 * при показе он разворачивается в колонку недели и колонки прошедших дней —
 * три полные недели плюс текущая, недели сворачиваются до итога. Здесь — реестр
 * календарей и общие для них ключи; заказы считаются по-своему (ряд
 * `daily_orders_30d` уже есть у товара), остальные три берут ряды из
 * `/api/day-calendars` (см. lib/day-calendars).
 *
 * Чем отличаются итоги недели:
 *  - заказы — сумма за неделю;
 *  - остатки — МИНИМУМ за неделю (сумма остатков по дням бессмысленна, а
 *    минимум показывает, до чего допродавались);
 *  - цена — средняя за неделю;
 *  - реклама — расход за неделю и ДРР за неделю (расход ÷ выручка).
 */

export type WeekCalendarMetric = "orders" | "stock" | "price" | "ads";

export type WeekCalendarDef = {
  metric: WeekCalendarMetric;
  /** Ключ-маркер в сохранённом наборе колонок. */
  marker: string;
  dayPrefix: string;
  sumPrefix: string;
  /** Название колонки-маркера в редакторе. */
  label: string;
  /** Чем календарь считает день: подпись в заголовках и подсказках («Заказы», «Остаток»). */
  dayWord: string;
  /** Подпись колонки итога недели («Неделя», «Минимум», «Средняя»). */
  sumLabel: string;
  dayWidth: number;
  sumWidth: number;
  /** Источники данных: ряд значений плюс дневник для точек и окна дня. */
  sources: DataDemandSource[];
};

export const WEEK_CALENDARS: Record<WeekCalendarMetric, WeekCalendarDef> = {
  orders: {
    metric: "orders",
    marker: ORDERS_WEEKS_MARKER_KEY,
    dayPrefix: ORDERS_WEEK_DAY_PREFIX,
    sumPrefix: ORDERS_WEEK_SUM_PREFIX,
    label: "Заказы по неделям",
    dayWord: "Заказы",
    sumLabel: ORDERS_WEEK_SUM_LABEL,
    dayWidth: ORDERS_WEEK_DAY_COLUMN_WIDTH,
    sumWidth: ORDERS_WEEK_SUM_COLUMN_WIDTH,
    sources: ["orders", "actionDiary"],
  },
  stock: {
    metric: "stock",
    marker: "stock_weeks",
    dayPrefix: "stock_week_day:",
    sumPrefix: "stock_week_sum:",
    label: "Остатки по неделям",
    dayWord: "Остаток",
    sumLabel: "Минимум",
    dayWidth: 62,
    sumWidth: 84,
    sources: ["stockDays", "orders", "actionDiary"],
  },
  price: {
    metric: "price",
    marker: "price_weeks",
    dayPrefix: "price_week_day:",
    sumPrefix: "price_week_sum:",
    label: "Цена по дням",
    dayWord: "Цена",
    sumLabel: "Средняя",
    dayWidth: 70,
    sumWidth: 84,
    sources: ["priceDays", "orders", "actionDiary"],
  },
  ads: {
    metric: "ads",
    marker: "ads_weeks",
    dayPrefix: "ads_week_day:",
    sumPrefix: "ads_week_sum:",
    label: "Реклама по дням",
    dayWord: "Реклама",
    sumLabel: "Неделя",
    dayWidth: 70,
    sumWidth: 84,
    sources: ["adsDays", "orders", "actionDiary"],
  },
};

export const WEEK_CALENDAR_LIST: readonly WeekCalendarDef[] = Object.values(WEEK_CALENDARS);
export const WEEK_CALENDAR_MARKER_KEYS: readonly string[] = WEEK_CALENDAR_LIST.map(def => def.marker);

/** Сколько дней ряда нужно календарю: окно как у дневника (3 недели + текущая). */
export const WEEK_CALENDAR_DAYS = ORDERS_WEEKS_DIARY_DAYS;

/** ДРР, с которого день рекламы подсвечивается красным (как «выше нормы» на панели). */
export const ADS_DRR_ALERT_PERCENT = 20;

export function weekCalendarOfMarker(key: string): WeekCalendarDef | null {
  return WEEK_CALENDAR_LIST.find(def => def.marker === key) ?? null;
}

export function isWeekCalendarMarkerKey(key: string): boolean {
  return weekCalendarOfMarker(key) !== null;
}

export type WeekCalendarKey = {
  metric: WeekCalendarMetric;
  kind: "day" | "sum";
  /** День — его дата; итог недели — её понедельник. */
  date: string;
};

/** Разбор ключа дня или итога любого календаря; маркер и чужие ключи — null. */
export function parseWeekCalendarKey(key: string): WeekCalendarKey | null {
  for (const def of WEEK_CALENDAR_LIST) {
    if (key.startsWith(def.dayPrefix)) {
      const date = key.slice(def.dayPrefix.length);
      return isIsoDateKey(date) ? { metric: def.metric, kind: "day", date } : null;
    }
    if (key.startsWith(def.sumPrefix)) {
      const date = key.slice(def.sumPrefix.length);
      return isIsoDateKey(date) ? { metric: def.metric, kind: "sum", date } : null;
    }
  }
  return null;
}

export function isWeekCalendarColumnKey(key: string): boolean {
  return parseWeekCalendarKey(key) !== null;
}

export function isWeekCalendarSumColumnKey(key: string): boolean {
  return parseWeekCalendarKey(key)?.kind === "sum";
}

export function isWeekCalendarDayColumnKey(key: string): boolean {
  return parseWeekCalendarKey(key)?.kind === "day";
}

/** Календарь, которому принадлежит ключ дня, итога или маркер. */
export function weekCalendarOfColumnKey(key: string): WeekCalendarDef | null {
  const parsed = parseWeekCalendarKey(key);
  return parsed ? WEEK_CALENDARS[parsed.metric] : weekCalendarOfMarker(key);
}

/** Понедельник недели колонки и её календарь — для автогрупп таблицы. */
export function weekCalendarWeekOfColumnKey(key: string): { metric: WeekCalendarMetric; weekStart: string } | null {
  const parsed = parseWeekCalendarKey(key);
  if (!parsed) return null;
  return { metric: parsed.metric, weekStart: parsed.kind === "sum" ? parsed.date : ordersWeekStartOf(parsed.date) };
}

export function weekCalendarDayKey(metric: WeekCalendarMetric, date: string): string {
  return `${WEEK_CALENDARS[metric].dayPrefix}${date}`;
}

export function weekCalendarSumKey(metric: WeekCalendarMetric, weekStart: string): string {
  return `${WEEK_CALENDARS[metric].sumPrefix}${weekStart}`;
}

/** Колонки недели одного календаря в порядке показа: итог, затем дни. */
export function weekCalendarColumnKeys(metric: WeekCalendarMetric, week: OrdersWeek): string[] {
  return [weekCalendarSumKey(metric, week.start), ...week.days.map(date => weekCalendarDayKey(metric, date))];
}

/** Ширина колонки по умолчанию для ключа дня или итога; чужой ключ — null. */
export function weekCalendarDefaultWidth(key: string): number | null {
  const parsed = parseWeekCalendarKey(key);
  if (!parsed) return null;
  const def = WEEK_CALENDARS[parsed.metric];
  return parsed.kind === "sum" ? def.sumWidth : def.dayWidth;
}

/**
 * Маркеры всех календарей → колонки дней и недель на их местах. Уже
 * развёрнутые ключи остаются на своих местах без дублей.
 */
export function expandWeekCalendarColumns(columns: readonly string[], anchorDate: string): string[] {
  if (!columns.some(key => isWeekCalendarMarkerKey(key))) return [...columns];
  const weeks = buildOrdersWeeks(anchorDate);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of columns) {
    const def = weekCalendarOfMarker(key);
    if (def) {
      for (const week of weeks) {
        for (const weekKey of weekCalendarColumnKeys(def.metric, week)) {
          if (!seen.has(weekKey)) { seen.add(weekKey); out.push(weekKey); }
        }
      }
    } else if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

/** Колонки дней и недель → маркер своего календаря на месте первой из них (для редактора). */
export function collapseWeekCalendarColumns(columns: readonly string[]): string[] {
  const out: string[] = [];
  const collapsed = new Set<WeekCalendarMetric>();
  for (const key of columns) {
    const parsed = parseWeekCalendarKey(key);
    if (parsed) {
      if (!collapsed.has(parsed.metric)) {
        collapsed.add(parsed.metric);
        out.push(WEEK_CALENDARS[parsed.metric].marker);
      }
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

// ── Ряды значений по дням (остатки, цена, реклама) ──────────────────────────

/**
 * Ряд по дням, как его отдаёт /api/day-calendars: `days` по возрастанию
 * (последний — вчера по Москве на момент ответа), значения — по тем же индексам.
 * null — записи за день нет (товар появился позже, история ещё не накопилась).
 */
export type DayCalendarSeries = {
  days: string[];
  values: Record<string, Array<number | null>>;
};

export type AdsCalendarRow = { spend: number[]; revenue: number[]; orders: number[] };

export type AdsCalendarSeries = {
  days: string[];
  values: Record<string, AdsCalendarRow>;
};

/** Индекс даты в ряду; -1 — даты в окне нет. */
export function seriesIndexOf(days: readonly string[], date: string): number {
  if (days.length === 0) return -1;
  const index = days.length - 1 - daysBetweenIso(date, days[days.length - 1]);
  return index >= 0 && index < days.length ? index : -1;
}

export function seriesValueOnDate(values: ReadonlyArray<number | null> | null | undefined, days: readonly string[], date: string): number | null {
  if (!values) return null;
  const index = seriesIndexOf(days, date);
  if (index < 0) return null;
  const value = values[index];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Даты недели, уже прошедшие к `anchorDate` (включительно). */
export function weekDatesUntil(weekStart: string, anchorDate: string): string[] {
  const out: string[] = [];
  for (let offset = 0; offset < 7; offset++) {
    const date = addDaysIso(weekStart, offset);
    if (date > anchorDate) break;
    out.push(date);
  }
  return out;
}

/** Значения ряда по дням недели до `anchorDate`; дней без записи в списке нет. */
export function seriesWeekValues(values: ReadonlyArray<number | null> | null | undefined, days: readonly string[], weekStart: string, anchorDate: string): number[] {
  const out: number[] = [];
  for (const date of weekDatesUntil(weekStart, anchorDate)) {
    const value = seriesValueOnDate(values, days, date);
    if (value !== null) out.push(value);
  }
  return out;
}

export function minOf(values: readonly number[]): number | null {
  return values.length ? values.reduce((min, value) => Math.min(min, value), Infinity) : null;
}

export function averageOf(values: readonly number[]): number | null {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

/** ДРР в процентах: расход ÷ выручка с рекламы. Без выручки ДРР нет (null). */
export function adsDrr(spend: number, revenue: number): number | null {
  if (!(revenue > 0)) return null;
  return spend / revenue * 100;
}

/** День рекламы «красный»: ДРР выше порога или расход есть, а выручки нет. */
export function isAdsAlert(spend: number, revenue: number): boolean {
  if (spend <= 0) return false;
  const drr = adsDrr(spend, revenue);
  return drr === null || drr >= ADS_DRR_ALERT_PERCENT;
}

/**
 * Тепло дня рекламы — как у заказов (lib/orders-weeks-columns.ordersWeekHeatColor),
 * но тёплым тоном: реклама в таблице оранжевая.
 */
const ADS_HEAT_RGB = "214,150,80";
export function adsWeekHeatColor(value: number, max: number, strong = false): string | undefined {
  if (value <= 0 || max <= 0) return undefined;
  const share = Math.min(1, value / max);
  const alpha = strong ? 0.14 + share * 0.40 : 0.05 + share * 0.24;
  return `rgba(${ADS_HEAT_RGB},${alpha.toFixed(2)})`;
}

/** Фон дня без остатка — тёплый, чтобы дыры в наличии читались с расстояния. */
export const STOCK_ZERO_DAY_BG = "rgba(214,120,90,0.16)";
